import Spine from '@finviz/spine'
import { isSameMonth, isSameWeek } from 'date-fns'
import queryString from 'query-string'

import { Instrument, ObjectHash, TodoObjectHashAnyType } from '../../types/shared'
import { IntradayTimeframeInterval, TIMEFRAME } from '../constants/common'
import type { ICOT } from '../indicators/cot'
import { dateFromUnixTimestamp, dateStringFromDate } from '../utils'
import { getIsAbortError } from '../utils/abort_controller'
import { getIsPersistDrawingsEnabled, getPositionXFromTimestamp, getTimestampFromPositionX } from '../utils/chart'
import fetchApi, { NotFoundError } from '../utils/fetch_api'
import { getIsSSr } from '../utils/helpers'
import { QuoteFetchType, QuoteFetchTypeUrlOption, QuoteUrlOptions } from './quote/constants'
import { deleteCachedQuote, getCachedQuote, setCachedQuote } from './quoteCache'

const quoteRefreshChecksBeforeFullRefresh = 15

type TodoPattern = TodoObjectHashAnyType

export interface TickerTimeframesProps {
  ticker: string
  instrument: string
  timeframe: string
}

interface QuoteFetchOptions {
  aftermarket?: boolean
  premarket?: boolean
  patterns?: boolean
  maxBars?: number
}

interface QuoteInternalOptions {
  cachePredicate?: (quote: Quote) => boolean
  fetchNewDataOnCachedQuote?: boolean
}

interface UrlOptions extends QuoteFetchOptions {
  ticker: string
  instrument: string
  timeframe: string
}

interface DataResponse {
  COTs: COTData
  name: string
  ticker: string
  instrument: string
  interval: number
  timeframe: string
  open: number[]
  high: number[]
  low: number[]
  close: number[]
  date: number[]
  volume: number[]
  lastOpen: number
  lastHigh: number
  lastLow: number
  lastClose: number
  lastDate: number
  lastVolume: number
  dataId: string
  prevClose: number
  afterClose: number
  afterChange: number
  drawMinutesPerDay: number
  marketStartMinutes: number
  premarketLengthMinutes: number
  aftermarketLengthMinutes: number
  aftermarket: boolean
  premarket: boolean
  hasPatterns: boolean
  patterns: Array<TodoPattern>
  patternsMinRange: number
  patternsMaxRange: number
  relativeVolume: number
  isIntraday: boolean
  noNewData?: boolean
}

interface COTData {
  [code: string]: ICOT
}

const emptyQuote = {
  open: [],
  high: [],
  low: [],
  close: [],
  volume: [],
  date: [],
  fetchedAt: -1,
  numberOfRefreshChecks: 0,
  isFetching: false,
  is404: false,
  COTs: undefined,
  name: undefined,
  drawMinutesPerDay: undefined,
  marketStartMinutes: undefined,
  premarketLengthMinutes: undefined,
  aftermarketLengthMinutes: undefined,
  patternsMinRange: undefined,
  patternsMaxRange: undefined,
  relativeVolume: undefined,
  lastOpen: undefined,
  lastHigh: undefined,
  lastLow: undefined,
  lastClose: undefined,
  lastVolume: undefined,
  dataId: undefined,
  lastDate: undefined,
  prevClose: undefined,
  afterClose: undefined,
  afterChange: undefined,
}

interface ICacheAvailabilityCheckFunctionsArguments {
  quote: Quote
  options?: QuoteFetchOptions & QuoteInternalOptions
}

const doesQuoteAndOptionsMatch = ({ quote, options }: ICacheAvailabilityCheckFunctionsArguments) =>
  !quote.premarket === !options?.premarket &&
  !quote.aftermarket === !options?.aftermarket &&
  !quote.hasPatterns === !options?.patterns

const doesQuoteAndFetchParamsMatch = ({
  quote,
  ticker,
  instrument,
  timeframe,
}: {
  quote: Quote
  ticker: string
  instrument: Instrument
  timeframe: TIMEFRAME
}) => quote.ticker === ticker && quote.instrument === instrument && quote.timeframe === timeframe

// either we don't care about matching uuid at all, or they have to match
const isSameOrNoChartUuid = ({ quote, uuid }: { quote: Quote; uuid?: string }) => !uuid || quote.chartUuid === uuid

const isCacheAllowed = ({ quote, options }: ICacheAvailabilityCheckFunctionsArguments) =>
  !options?.cachePredicate || options.cachePredicate(quote)

const isNotEmptyQuote = (quote: Quote) => quote.fetchedAt !== -1 || quote.isFetching

export type QuoteGetParams = {
  ticker: string
  instrument: Instrument
  timeframe: TIMEFRAME
  options?: QuoteFetchOptions & QuoteInternalOptions
  abortController?: null | AbortController
  fetchFunc?: any
  shouldUseCache?: boolean
  chartUuid?: string
}

class Quote extends Spine.Model {
  static initClass() {
    this.configure(
      'Quote',
      'ticker',
      'name',
      'instrument',
      'timeframe',
      'open',
      'high',
      'low',
      'close',
      'date',
      'volume',
      'lastOpen',
      'lastHigh',
      'lastLow',
      'lastClose',
      'lastDate',
      'lastVolume',
      'dataId',
      'prevClose',
      'COTs',
      'afterClose',
      'afterChange',
      'drawMinutesPerDay',
      'marketStartMinutes',
      'premarketLengthMinutes',
      'aftermarketLengthMinutes',
      'aftermarket',
      'premarket',
      'hasPatterns',
      'patterns',
      'patternsMinRange',
      'patternsMaxRange',
      'relativeVolume',
      'fetchedAt',
      'isIntraday',
      'interval',
      'drawDays',
      'barIndex',
      'fetchingPromise',
      'isFetching',
      'wasFetchAborted',
      'chartUuid',
      'numberOfRefreshChecks',
      'maxBars',
      'ideaID',
      'is404'
    )
  }

  static async get({
    ticker: tickerRaw,
    instrument,
    timeframe,
    options,
    abortController,
    fetchFunc,
    shouldUseCache = true,
    chartUuid,
  }: QuoteGetParams): Promise<Quote> {
    const ticker = tickerRaw.replace('@', '')
    const [quote] = shouldUseCache
      ? Quote.select<Quote>(
          (q) =>
            doesQuoteAndFetchParamsMatch({ quote: q, ticker, instrument, timeframe }) &&
            isSameOrNoChartUuid({ quote: q, uuid: chartUuid }) &&
            isNotEmptyQuote(q) &&
            isCacheAllowed({ quote: q, options }) &&
            (q.instrument !== Instrument.Stock ||
              Number.isInteger(q.ideaID) ||
              doesQuoteAndOptionsMatch({ quote: q, options }))
        )
      : []
    if (quote) {
      if (quote.isFetching) {
        await quote.getResolvedFetchingPromise()
      } else if (options?.fetchNewDataOnCachedQuote) {
        await quote.fetchData({ fetchType: QuoteFetchType.NewerData, abortController, fetchFunc })
      }
      return quote
    }

    const newQuote = Quote.create<Quote>({
      ...emptyQuote,
      ticker,
      timeframe,
      instrument,
      chartUuid,
      maxBars: options?.maxBars,
      premarket: options?.premarket,
      aftermarket: options?.aftermarket,
      hasPatterns: options?.patterns,
      patterns: options?.patterns,
    })

    await newQuote.fetchData({ fetchType: QuoteFetchType.Refetch, abortController, fetchFunc })

    return newQuote
  }

  static async getAll(tickersAndTimeframes: Array<{ ticker: string; instrument: Instrument; timeframe: TIMEFRAME }>) {
    // TODO: fetch in 1 request
    if (tickersAndTimeframes.length === 0) {
      return {}
    }

    try {
      await Promise.all(
        tickersAndTimeframes.map(({ ticker, instrument, timeframe }) =>
          this.get({ ticker: ticker, instrument, timeframe })
        )
      )
      const data = tickersAndTimeframes.reduce(
        (accumulator, currentValue) => ({
          ...accumulator,
          [currentValue.ticker]: Quote.select(
            (q: Quote) =>
              q.ticker === currentValue.ticker &&
              q.instrument === currentValue.instrument &&
              q.timeframe === currentValue.timeframe
          )[0],
        }),
        {}
      )
      return data
    } catch (err) {
      // TODO: handle somehow
    }
  }

  declare ticker: string
  declare name: string
  declare instrument: Instrument
  declare interval: number
  declare timeframe: TIMEFRAME
  declare open: number[]
  declare high: number[]
  declare low: number[]
  declare close: number[]
  declare date: number[]
  declare volume: number[]
  declare lastOpen: number
  declare lastHigh: number
  declare lastLow: number
  declare lastClose: number
  declare lastDate: number
  declare lastVolume: number
  declare dataId: string
  declare prevClose: number
  declare COTs: COTData
  declare afterClose: number
  declare afterChange: number
  declare drawMinutesPerDay: number
  declare marketStartMinutes: number
  declare premarketLengthMinutes: number
  declare aftermarketLengthMinutes: number
  declare hasPatterns: boolean
  declare patterns: Array<TodoPattern> | undefined
  declare patternsMinRange: number
  declare patternsMaxRange: number
  declare relativeVolume: number
  declare isIntraday: boolean
  declare drawDays?: number
  declare barIndex: number[]
  declare chartUuid?: string
  declare aftermarket?: boolean
  declare premarket?: boolean
  declare fetchedAt: number
  declare numberOfRefreshChecks: number
  declare isFetching: boolean
  declare fetchingPromise?: Promise<void>
  declare wasFetchAborted: boolean
  declare maxBars?: number
  declare ideaID?: number
  declare is404?: boolean

  async fetchRequest({
    abortController,
    fetchFunc,
    options = { type: QuoteFetchTypeUrlOption.New },
  }: {
    abortController?: null | AbortController
    fetchFunc?: any
    options?: QuoteUrlOptions
  }) {
    let data: DataResponse | null = null
    let is404 = false
    let wasFetchAborted = false

    const fetchingPromise = fetchApi<DataResponse>({
      location: this.getUrl(options),
      throwOnStatusCodes: [404],
      abortController,
      fetchFunc,
    })

    this.updateAttributes({
      isFetching: true,
      fetchingPromise,
    })

    try {
      data = await fetchingPromise
    } catch (err) {
      if (getIsAbortError(err as Error)) {
        wasFetchAborted = true
      }
      is404 = err instanceof NotFoundError
      // Ignore network/notfound errors
    }

    this.updateAttributes({
      ...(!data && !wasFetchAborted && options.type === QuoteFetchTypeUrlOption.New ? emptyQuote : {}),
      is404,
      wasFetchAborted,
      isFetching: false,
      fetchedAt: data ? Date.now() : this.fetchedAt,
    })

    return data
  }

  async isPossibleToFetchSequentialData({
    abortController,
    fetchFunc,
  }: {
    abortController?: null | AbortController
    fetchFunc?: any
  } = {}) {
    const data = await this.fetchRequest({ abortController, fetchFunc })
    if (data && data.date.length > 0 && this.date.length > 0) {
      return this.date[this.date.length - 1] >= data.date[0]
    }
    return false
  }

  async fetchData({
    fetchType,
    abortController,
    fetchFunc,
  }: {
    fetchType: QuoteFetchType
    abortController?: null | AbortController
    fetchFunc?: any
  }) {
    let options: QuoteUrlOptions
    if (fetchType === QuoteFetchType.Refetch) {
      options = { type: QuoteFetchTypeUrlOption.New }
    } else if (fetchType === QuoteFetchType.NewerData) {
      options = { type: QuoteFetchTypeUrlOption.Newer }
      if ((this.numberOfRefreshChecks ?? 0) < quoteRefreshChecksBeforeFullRefresh) {
        options.dataId = this.dataId
      }
    } else {
      return
    }

    const data = await this.fetchRequest({ abortController, fetchFunc, options })

    if (!data || data.noNewData === true) {
      this.updateAttributes({
        numberOfRefreshChecks: (this.numberOfRefreshChecks ?? 0) + 1,
      })
      return
    }
    if (this.timeframe !== data.timeframe || this.ticker !== data.ticker) {
      return
    }
    const newQuoteData = {
      COTs: data.COTs,
      name: data.name,
      open: data.open,
      high: data.high,
      low: data.low,
      close: data.close,
      volume: data.volume,
      date: data.date,
      drawMinutesPerDay: data.drawMinutesPerDay,
      marketStartMinutes: data.marketStartMinutes,
      premarketLengthMinutes: data.premarketLengthMinutes,
      aftermarketLengthMinutes: data.aftermarketLengthMinutes,
      patterns: data.patterns,
      patternsMinRange: data.patternsMinRange,
      patternsMaxRange: data.patternsMaxRange,
      relativeVolume: data.relativeVolume,
      lastOpen: data.lastOpen,
      lastHigh: data.lastHigh,
      lastLow: data.lastLow,
      lastClose: data.lastClose,
      lastVolume: data.lastVolume,
      dataId: data.dataId,
      lastDate: data.lastDate,
      prevClose: data.prevClose,
      afterClose: data.afterClose,
      afterChange: data.afterChange,
      numberOfRefreshChecks: 0,
    }
    if (
      !(
        fetchType === QuoteFetchType.Refetch ||
        [Instrument.Futures, Instrument.Forex, Instrument.Crypto].includes(this.instrument)
      )
    ) {
      /*
       * Merges current and new data taking date as index
       */
      let AIndex = 0
      let BIndex = 0
      let mergeIndex = -1
      while (AIndex < this.date.length || BIndex < data.date.length) {
        const ADate = this.date[AIndex]
        const BDate = data.date[BIndex]
        if (BDate === undefined) {
          break
        } else if (ADate === BDate) {
          // Same date, update OHLCV
          this.open[AIndex] = data.open[BIndex]
          this.high[AIndex] = data.high[BIndex]
          this.low[AIndex] = data.low[BIndex]
          this.close[AIndex] = data.close[BIndex]
          this.volume[AIndex] = data.volume[BIndex]
          AIndex++
          BIndex++
        } else if (ADate > BDate || ADate === undefined) {
          // New bar for a date not yet in our dataset
          // If weekly or monthly timeframe & wasn't merged on AIndex
          if (mergeIndex === -1 && [TIMEFRAME.w, TIMEFRAME.m].includes(this.timeframe)) {
            const prevAIndex = AIndex - 1
            const prevDate = dateFromUnixTimestamp(this.date[prevAIndex])
            const newDate = dateFromUnixTimestamp(data.date[BIndex])
            // If merge bar for weekly is sameWeek or for monthly is sameMonth perform merge on AIndex
            if (
              (TIMEFRAME.w === this.timeframe && isSameWeek(prevDate, newDate, { weekStartsOn: 1 })) ||
              (TIMEFRAME.m === this.timeframe && isSameMonth(prevDate, newDate))
            ) {
              this.date[prevAIndex] = data.date[BIndex]
              this.open[prevAIndex] = data.open[BIndex]
              this.high[prevAIndex] = data.high[BIndex]
              this.low[prevAIndex] = data.low[BIndex]
              this.close[prevAIndex] = data.close[BIndex]
              this.volume[prevAIndex] = data.volume[BIndex]
              mergeIndex = prevAIndex
              // Incrementing BIndex only because we're mergin arrays on AIndex
              BIndex++
              continue
            }
          }
          this.date.splice(AIndex, 0, data.date[BIndex])
          this.open.splice(AIndex, 0, data.open[BIndex])
          this.high.splice(AIndex, 0, data.high[BIndex])
          this.low.splice(AIndex, 0, data.low[BIndex])
          this.close.splice(AIndex, 0, data.close[BIndex])
          this.volume.splice(AIndex, 0, data.volume[BIndex])
          // Incrementing AIndex because we're changing the array in-place
          AIndex++
          BIndex++
        } else if (ADate < BDate) {
          AIndex++
        } else {
          console.warn('fetchNewerData merge warning')
          window.Sentry?.captureMessage('quote.ts fetchNewerData merge warning', {
            extra: {
              thisDateLength: this.date.length,
              dataDateLength: data.date.length,
              AIndex,
              BIndex,
              ADate,
              BDate,
            },
          })
        }
      }

      newQuoteData.date = this.date
      newQuoteData.open = this.open
      newQuoteData.high = this.high
      newQuoteData.low = this.low
      newQuoteData.close = this.close
      newQuoteData.volume = this.volume
    }

    const hasUpdatedSuccessfully = this.updateAttributes(newQuoteData)
    // https://github.com/finvizhq/charts/issues/510
    if (!hasUpdatedSuccessfully) {
      // attempt to find if this.model.quote() => null is comming from this.save() validation fail
      window.Sentry?.captureMessage('quote.ts fetchNewerData save() validation failed', { extra: this })
    }
    if (!this) {
      // or this somehow became null / falsy
      window.Sentry?.captureMessage('quote.ts fetchNewerData this is falsy', { extra: this })
    }
    this.trigger(fetchType, this, fetchType)
  }

  async getResolvedFetchingPromise() {
    try {
      return await this.fetchingPromise
    } catch {
      // do nothing
    }
  }

  fetchOlderData() {
    return // not yet implemented
    // TODO cancel previous request
  }

  load(atts: ObjectHash) {
    super.load(atts)
    const { fetchedAt = 0 } = atts
    const { cachedAt } = (getCachedQuote({ cid: this.cid }) as { cachedAt: number } | null) || {}
    if (this.timeframe && cachedAt !== fetchedAt) {
      setCachedQuote({ cid: this.cid, key: 'cachedAt', value: fetchedAt, fetchedAt })
      this.isIntraday = [
        TIMEFRAME.i1,
        TIMEFRAME.i3,
        TIMEFRAME.i5,
        TIMEFRAME.i10,
        TIMEFRAME.i15,
        TIMEFRAME.i30,
        TIMEFRAME.h,
        TIMEFRAME.h2,
        TIMEFRAME.h4,
      ].includes(this.timeframe)
      this.interval = this.isIntraday
        ? IntradayTimeframeInterval[this.timeframe as keyof typeof IntradayTimeframeInterval]
        : 0
      this.drawDays = this.getDrawDays()
      this.barIndex = this.getBarIndexes()
    }
    return this
  }

  destroy(options?: ObjectHash) {
    super.destroy(options)
    deleteCachedQuote({ cid: this.cid })
    return this
  }

  getDrawDays() {
    if (this.instrument !== Instrument.Stock) {
      return Infinity
    }
    switch (this.timeframe) {
      case TIMEFRAME.i1:
      case TIMEFRAME.i3:
      case TIMEFRAME.i5:
      case TIMEFRAME.i10:
        return 10
      case TIMEFRAME.i15:
      case TIMEFRAME.i30:
      case TIMEFRAME.h:
      case TIMEFRAME.h2:
      case TIMEFRAME.h4:
        return 15
      default:
        break
    }
  }

  getHeikinAshiData() {
    const cachedHeikinAshiData = getCachedQuote({ cid: this.cid, key: 'heikinAshiData', fetchedAt: this.fetchedAt })
    if (cachedHeikinAshiData) {
      return cachedHeikinAshiData as unknown as { open: number[]; close: number[] }
    }

    const HAClose = this.open.map((_, i) => (this.open[i] + this.high[i] + this.close[i] + this.low[i]) / 4)
    const HAOpen = this.open.reduce(
      (acc, open, i) => [...acc, (i === 0 ? open + HAClose[0] : acc[i - 1] + HAClose[i - 1]) / 2],
      [] as number[]
    )

    const heikAshiData = { open: HAOpen, close: HAClose }

    setCachedQuote({ cid: this.cid, key: 'heikinAshiData', value: heikAshiData, fetchedAt: this.fetchedAt })

    return heikAshiData
  }

  getDayToOffset(): ObjectHash<number> {
    const cachedDayToOffset = getCachedQuote({ cid: this.cid, key: 'dayToOffset', fetchedAt: this.fetchedAt })
    if (cachedDayToOffset) {
      return cachedDayToOffset as unknown as ObjectHash<number>
    }
    const dayToOffset: ObjectHash = {}
    let day = 0
    let lastDay
    for (let i = 0, end = this.date.length, asc = end >= 0; asc ? i < end : i > end; asc ? i++ : i--) {
      const date = dateFromUnixTimestamp(this.date[i])
      const dateString = dateStringFromDate(date)
      if (dateString !== lastDay) {
        dayToOffset[dateString] = day
        day++
        lastDay = dateString
      }
    }
    // for ofscreen renderer we need to add lastDate to have correct dayToOffset because if
    // premarket=false in fetch query there isn't other way to distinguish that
    if (getIsSSr() && this.lastDate) {
      // YYYYMMDD / 20211118 => YYYY-MM-DD
      const [yyyy, mm, dd] = [~~(this.lastDate / 10000), ~~(this.lastDate / 100) % 100, this.lastDate % 100]
      const lastDate = `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`

      if (lastDay !== lastDate) {
        dayToOffset[lastDate] = day
      }
    }

    setCachedQuote({ cid: this.cid, key: 'dayToOffset', value: dayToOffset, fetchedAt: this.fetchedAt })
    return dayToOffset
  }

  getBarIndexes() {
    if (!this.isIntraday || this.instrument !== Instrument.Stock) {
      return Array.from({ length: this.date.length }).map((_, index) => index)
    }

    const barIndex = []
    const dayToOffset = this.getDayToOffset()

    for (let i = 0; i < this.date.length; i += 1) {
      const date = dateFromUnixTimestamp(this.date[i])
      let x = Math.ceil((date.getHours() * 60 + date.getMinutes() - this.marketStartMinutes) / this.interval)
      x += dayToOffset[dateStringFromDate(date)] * this.getBarsInDay()! // add days
      barIndex[i] = x
    }

    return barIndex
  }

  getBarIndex(i: number) {
    if (!this.isIntraday || this.instrument !== Instrument.Stock) {
      return i
    }
    return this.barIndex[i]
  }

  getBarsInDay() {
    if (!this.isIntraday || this.instrument !== Instrument.Stock) {
      return
    }
    return Math.ceil(this.drawMinutesPerDay / this.interval)
  }

  getDataByBarIndex(key: keyof Quote, index: number) {
    if (this.barIndex) {
      for (let i = 0; i < this.barIndex.length; i++) {
        if (this.barIndex[i] === index) {
          const a = this[key as keyof Quote]! as number[]
          return a[i] ?? null
        }
      }
    }
    return null
  }

  getDateToIndex() {
    const cachedDateToIndex = getCachedQuote({ cid: this.cid, key: 'dateToIndex', fetchedAt: this.fetchedAt })
    if (cachedDateToIndex) {
      return cachedDateToIndex as unknown as ObjectHash<number>
    }
    const dateToIndex: ObjectHash<number> = {}
    for (let i = 0; i < this.date.length; i++) {
      dateToIndex[this.date[i]] = i
    }
    setCachedQuote({ cid: this.cid, key: 'dateToIndex', value: dateToIndex, fetchedAt: this.fetchedAt })
    return dateToIndex
  }

  getTimestampFomPositionX(positionX: number) {
    if (!getIsPersistDrawingsEnabled() || this.date.length === 0) {
      return 0
    }
    return getTimestampFromPositionX({
      positionX,
      ticker: this.ticker,
      timeframe: this.timeframe,
      dates: this.date,
      marketStartMinutes: this.marketStartMinutes,
      instrument: this.instrument,
      barsInDay: this.getBarsInDay(),
      isIntraday: this.isIntraday,
    })
  }

  getPositionXFromTimestamp(timestamp: number) {
    if (!getIsPersistDrawingsEnabled() || this.date.length === 0) {
      return 0
    }
    return getPositionXFromTimestamp({
      timestamp,
      ticker: this.ticker,
      timeframe: this.timeframe,
      dates: this.date,
      marketStartMinutes: this.marketStartMinutes,
      instrument: this.instrument,
      barsInDay: this.getBarsInDay(),
      isIntraday: this.isIntraday,
    })
  }

  getUrl(options: QuoteUrlOptions) {
    const config: UrlOptions = {
      ticker: this.ticker,
      instrument: this.instrument,
      timeframe: this.timeframe || 'd1',
      aftermarket: this.aftermarket,
      premarket: this.premarket,
      patterns: this.hasPatterns || undefined,
      maxBars: this.maxBars,
      ...options,
    }

    const isStock = config.instrument === Instrument.Stock
    const isBrowserElite = !getIsSSr() && document.location.host === 'elite.finviz.com'
    let domain = isBrowserElite && isStock ? 'https://api.finviz.com' : ''

    if (getIsSSr()) {
      domain = process.env.BE_API_URL!
    }

    return `${domain}/api/quote.ashx?${queryString.stringify({ rev: Date.now(), ...config })}`
  }

  getRawTicker() {
    return `${[Instrument.Crypto, Instrument.Forex, Instrument.Futures].includes(this.instrument) ? '@' : ''}${
      this.ticker
    }`
  }
}

Quote.initClass()

export default Quote
